import { useState } from "react";
import { NavLink, Outlet } from "react-router";
import type { Permission } from "@tcr/contracts";
import {
  BookOpen,
  Bot,
  ClipboardList,
  KeyRound,
  LogOut,
  Map as MapIcon,
  Menu,
  Network,
  ScrollText,
  ShieldAlert,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { useRealtimeStore } from "@/realtime/store";
import { cn, formatDateTime } from "@/lib/format";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { ThemeToggle } from "./ThemeToggle";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Bot;
  perm: Permission;
}

const MAIN: NavItem[] = [
  { to: "/fleet", label: "Fleet", icon: Bot, perm: "robot.view" },
  { to: "/missions", label: "Missions", icon: ClipboardList, perm: "mission.view" },
  { to: "/maps", label: "Maps & routes", icon: MapIcon, perm: "map.view" },
  { to: "/audit", label: "Audit log", icon: ScrollText, perm: "audit.view" },
];
const ADMIN: NavItem[] = [
  { to: "/admin/registry", label: "ROS interfaces", icon: Network, perm: "registry.manage" },
  { to: "/admin/errors", label: "Error catalog", icon: ShieldAlert, perm: "errors.manage" },
  { to: "/admin/users", label: "Users & roles", icon: Users, perm: "users.manage" },
  { to: "/admin/tokens", label: "API tokens", icon: KeyRound, perm: "users.manage" },
];

function LinkIndicator() {
  const link = useRealtimeStore((s) => s.link);
  // This is the browser's link to the platform, not any robot's connection.
  const label = link === "open" ? "Platform live" : link === "connecting" ? "Connecting…" : link === "reconnecting" ? "Reconnecting…" : "Platform offline";
  const color = link === "open" ? "bg-green-500" : link === "connecting" || link === "reconnecting" ? "bg-amber-500 animate-pulse" : "bg-red-500";
  return (
    <span className="inline-flex items-center gap-2 text-xs text-subtle" title="Live updates from the platform to this browser. Each robot's own connection is shown on the robot.">
      <span className={cn("size-2 rounded-full", color)} aria-hidden />
      {label}
    </span>
  );
}

interface VersionInfo {
  apiVersion: string;
  buildRevision: string | null;
  builtAt: string | null;
  contractVersion: string;
}

/** Which build this platform is running — what support needs to know first. */
function BuildInfo() {
  const v = useQuery({ queryKey: ["platform-version"], queryFn: () => api.get<VersionInfo>("/version"), staleTime: 5 * 60_000, retry: false });
  if (!v.data) return null;
  const d = v.data;
  return (
    <p className="mt-1 truncate px-2 text-[11px] text-subtle" title={`API ${d.apiVersion} · contract ${d.contractVersion}${d.builtAt ? ` · built ${formatDateTime(d.builtAt)}` : ""}`}>
      Platform {d.buildRevision ? <span className="font-mono">{d.buildRevision}</span> : "build not recorded"} · API {d.apiVersion}
    </p>
  );
}

function NavSection({ items, title, onNavigate }: { items: NavItem[]; title?: string; onNavigate?: () => void }) {
  const { permissions } = useAuth();
  const visible = items.filter((i) => permissions.has(i.perm));
  if (!visible.length) return null;
  return (
    <div className="space-y-0.5">
      {title && <p className="px-3 pt-4 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">{title}</p>}
      {visible.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors",
              isActive ? "bg-primary/10 text-primary" : "text-muted hover:bg-surface-2 hover:text-fg",
            )
          }
        >
          <i.icon className="size-4.5 shrink-0" aria-hidden />
          {i.label}
        </NavLink>
      ))}
    </div>
  );
}

export function AppShell() {
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const roles = [...new Set(me?.assignments.map((a) => a.role) ?? [])].join(", ");

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2 px-4">
        <img src="/favicon.svg" alt="" className="size-7" />
        <div className="leading-tight">
          <p className="text-sm font-semibold">TCR Operations</p>
          <p className="text-[11px] text-subtle">Toilet-cleaning robots</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 py-2" aria-label="Main">
        <NavSection items={MAIN} onNavigate={() => setOpen(false)} />
        <NavSection items={ADMIN} title="Administration" onNavigate={() => setOpen(false)} />
      </nav>
      <div className="border-t border-border p-3">
        <div className="mb-2">
          <LinkIndicator />
        </div>
        <p className="truncate text-sm font-medium">{me?.user.displayName}</p>
        <p className="truncate text-xs text-subtle">{me?.user.email}</p>
        {roles && <p className="truncate text-xs text-subtle capitalize">{roles.replace(/_/g, " ")}</p>}
        <a
          href="/api/docs"
          target="_blank"
          rel="noopener"
          className="mt-2 inline-flex h-8 w-full items-center gap-2 rounded-md px-2 text-xs text-subtle hover:bg-surface-2 hover:text-fg"
        >
          <BookOpen className="size-3.5" aria-hidden /> API reference
        </a>
        <BuildInfo />
        <ThemeToggle className="mt-2 w-full" />
        <button
          type="button"
          onClick={() => void logout()}
          className="mt-2 inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-border text-sm hover:bg-surface-2"
        >
          <LogOut className="size-4" aria-hidden /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-full">
      <aside className="hidden w-60 shrink-0 border-r border-border bg-surface lg:block">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r border-border bg-surface shadow-xl">
            <button type="button" className="absolute top-3 right-3 rounded p-1 hover:bg-surface-2" onClick={() => setOpen(false)} aria-label="Close menu">
              <X className="size-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-border bg-surface px-4 lg:hidden">
          <button type="button" className="rounded p-1.5 hover:bg-surface-2" onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu className="size-5" />
          </button>
          <span className="font-semibold">TCR Operations</span>
          <span className="ml-auto">
            <LinkIndicator />
          </span>
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <div className="mx-auto max-w-[1600px] p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
